import * as React from "react";
import {generic_value, VeboxModels} from "../../types/veboxModels";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {VeboxSelectionList} from "../selection_list/VeboxSelectionList";
import {Icon, icons, Tooltip} from "@vebox/etui-core";
import {VeboxExpandable} from "../expadable/VeboxExpandable";
import {AppTranslations} from "./translations/AppTranslations";

declare var AppTranslations: AppTranslations;

export const ROLE_END_USER_ADMIN = "ROLE_END_USER_ADMIN";
export const ROLE_ADMIN = "ROLE_ADMIN";
export const MANAGE_USERS = "MANAGE_USERS";
export const EDIT_DEVICE_SLAVE_DETAIL_SETTINGS = "EDIT_DEVICE_SLAVE_DETAIL_SETTINGS";
export const ACCESS_DEVICE_SLAVE_DETAIL_SETTINGS = "ACCESS_DEVICE_SLAVE_DETAIL_SETTINGS";
export const MANAGE_HIDDEN_USERS = 'MANAGE_HIDDEN_USERS';
export const ACCESS_DRIVEN_PROCESSES = 'ACCESS_DRIVEN_PROCESSES';

export interface PersonalData {
    availableNameEditing: boolean,
    availableSurnameEditing: boolean,
    availableEMailEditing: boolean,
    availableUsernameEditing: boolean,
    availableLocaleEditing: boolean,
    availableTimeZoneEditing: boolean,
    availableProfilePhotoEditing: boolean,
    allFalseExcludeUserName: boolean,
    allTrueExcludeUserName: boolean
}

export interface SettingsData {
    availableEnableEditing: boolean,
    availableVisibleEditing: boolean,
    availablePushNotificationEditing: boolean,
    availableEmailNotificationEditing: boolean,
    availableHierarchyVisibilityEditing: boolean,
    allFalse: boolean,
    allTrue: boolean,
    allTrueExcludeVisibleEditing: boolean
}

export interface RolesData {
    availableEditing: boolean,
    availableRoles: string[]
}

export interface AllowableChanges {
    personalData: PersonalData,
    settingsData: SettingsData,
    rolesData: RolesData
}

export const RoleDescriptionTooltip: React.FC = (props) => {

    const ref = React.useRef<HTMLDivElement>(null);

    const [marginTop, setMarginTop] = React.useState(0);

    React.useEffect(() => {

        if (ref.current) {
            const element = ref.current;
            const parent = element.parentElement;

            const contained = parent.scrollTop < element.offsetTop && element.offsetTop + element.clientHeight < parent.scrollTop + parent.clientHeight;

            setMarginTop(contained ? 0 : -46 - element.offsetHeight);
        }

    }, []);

    return (
        <div
            ref={ref}
            className={marginTop === 0 ? "bottom-tooltip" : "top-tooltip"}
            style={{marginTop}}
        >
            {props.children}
        </div>
    )
}

export const RoleListOption: React.FC<{
    role: VeboxModels.Role,
    disable?: boolean;
    disabledMessage?: string,
}> = ({disabledMessage, disable, role}) => {
    const collapsed = (expanded, setExpanded) => {
        const show = () => setExpanded(true);
        const hide = () => setExpanded(false);
        return (
            <VeboxSelectionList.Option
                key={role.code}
                value={role.code}
                className={"role-row"}
                disable={disable}
                disabledMessage={disabledMessage}
            >
                <div className={"font-weight-400 flx-row role-row-container flx-align-center " + (!role.visible ? " three-cols " : " two-cols ")}>
                    {role.name}
                    {!role.visible && (<Tooltip
                        key={role.code + " - tooltip"}
                        message={AppTranslations.usersManagement.invisibleRole}
                        placement={"left"}
                    >
                        <span className="material-icons-outlined default-cursor">visibility_off</span>
                    </Tooltip>)}
                    <div onMouseOver={show} onMouseOut={hide}>
                        <Icon glyph={icons.INFO_CIRCLE}/>
                    </div>
                </div>
            </VeboxSelectionList.Option>
        );
    }
    return (
        <VeboxExpandable
            key={role.code}
            collapsed={collapsed}
        >
            <RoleDescriptionTooltip>
                {role.description}
            </RoleDescriptionTooltip>
        </VeboxExpandable>
    )
}

export const RoleListElement: React.FC<{
    role: VeboxModels.Role,
    disable?: boolean;
    disabledMessage?: string
}> = (props) => {

    const {role} = props;
    const collapsed = (expanded, setExpanded) => {
        const show = () => setExpanded(true);
        const hide = () => setExpanded(false);
        return (
            <li className={"vebox-selection-list-option role-row"}>
                <div className={"vebox-selection-list-option-body"}>
                    <div className={"font-weight-400 flx-row role-row-container flx-align-center " + (!role.visible ? " three-cols " : " two-cols ")}>
                        {role.name}
                        {!role.visible && (<Tooltip
                            key={role.code + " - tooltip"}
                            message={AppTranslations.usersManagement.invisibleRole}
                            placement={"left"}
                        >
                            <span className="material-icons-outlined default-cursor">visibility_off</span>
                        </Tooltip>)}
                        <div onMouseOver={show} onMouseOut={hide}>
                            <Icon glyph={icons.INFO_CIRCLE}/>
                        </div>
                    </div>
                </div>
            </li>
        );
    }
    return (
        <VeboxExpandable
            key={role.code}
            collapsed={collapsed}
        >
            <RoleDescriptionTooltip>
                {role.description}
            </RoleDescriptionTooltip>
        </VeboxExpandable>
    )
}

export function getRolesAndPermissions(hierarchyString: string) {
    return new Promise<VeboxModels.Role[]>((res, rej) => {
        RestDataLoader.loadData(
            `users/current/roles/managed?${hierarchyString}`,
            {},
            (data) => res(data.list),
            () => rej(),
        );
    });
}

export function getAllowableChanges(userGuid: string) {
    return new Promise<AllowableChanges>((res, rej) => {
        RestDataLoader.loadData(
            `users/${userGuid}/allowableChanges`,
            {},
            res,
            () => rej(),
        );
    });
}

/**
 * Returns true if the biggerRole contains a superset of the permissions of the smaller one
 * @param biggerRole
 * @param smallerRole
 */
export const includesRole = (biggerRole: VeboxModels.Role, smallerRole: VeboxModels.Role): boolean => {
    return smallerRole?.permissions?.every(
        (smallerRolePermission) => roleContainsPermission(smallerRolePermission, biggerRole))
}
/**
 * Verify if a role contains a permission
 * @param permission
 * @param role
 */
export const roleContainsPermission = (permission: VeboxModels.Permission, role: VeboxModels.Role): boolean => {
    return role?.permissions.map((p) => p.code).includes(permission.code);
}

/**
 * Returns true if the given role is included in or includes at least one role in the roleList depends on the isIncluded boolean
 * @param roleToCheck
 * @param roleList
 * @param isIncluded
 */
export const isIncludedInOrIncludesAtLeastOneRole = (roleToCheck: VeboxModels.Role, roleList: VeboxModels.Role[],
                                                     isIncluded: boolean): boolean => {
    let iSIncludedInOrIncludes: boolean = false;
    roleList.forEach((r) => {
        if (isIncluded ? includesRole(r, roleToCheck) : includesRole(roleToCheck, r)) {
            iSIncludedInOrIncludes = true;
            return;
        }
    })
    return iSIncludedInOrIncludes;
}

/**
 * Returns the role from the role code
 * @param targetRole
 * @param rolesList
 */
export const getRoleFromRoleCode = (targetRole: string, rolesList: VeboxModels.Role[]): VeboxModels.Role => {
    return rolesList.find((r) => r.code === targetRole);
}

/**
 * Returns the list of selectedRoles without the selectedRoles that are 'contained' by the differentRole (differentRole contains a superset of permissions of the selectedRoles removed from the list)
 * @param differentRoleCode
 * @param selectedRoles
 * @param rolesManaged
 */
export const smartRoleSelection = (differentRoleCode: string, selectedRoles: string[],
                                   rolesManaged: VeboxModels.Role[]): string[] => {

    const diffRole: VeboxModels.Role = getRoleFromRoleCode(differentRoleCode, rolesManaged);
    return selectedRoles.filter(
        (r) => r === diffRole.code || !includesRole(diffRole, getRoleFromRoleCode(r, rolesManaged)));
}

/**
 * Returns the different role between newRolesCodes and prevRolesCodes
 * @param newRolesCodes
 * @param prevRolesCodes
 */
export const getDifferentRole = (newRolesCodes: string[], prevRolesCodes: string[]): string => {
    if (newRolesCodes.length > prevRolesCodes.length) {
        return newRolesCodes.filter((r) => prevRolesCodes.findIndex((r2) => r2 === r) === -1).pop()
    }
    return prevRolesCodes.filter((r) => newRolesCodes.findIndex((r2) => r2 === r) === -1).pop()
}

/**
 * Return true if the word match a role or a permission of a role
 * @param word
 */
export const isRoleMatchingWord = (word: string) => (role: VeboxModels.Role): boolean => {
    return role.name.toLowerCase().includes(word.toLowerCase())
        || role.description.toLowerCase().includes(word.toLowerCase())
}

/**
 * Returns true if the give role is manageable
 * @param currentRole
 * @param manageableRoles
 */
export const isManageableRole = (currentRole: VeboxModels.Role, manageableRoles: VeboxModels.Role[]): boolean => {
    return manageableRoles.findIndex((role) => role?.code === currentRole?.code) > -1
}

/**
 * Return true if the role should be disabled
 * @param currentRole
 * @param visibleRoles
 * @param manageableRoles
 * @param selectedRoles
 * @param isAdminRoleChecked
 */
export const disableRole = (currentRole: VeboxModels.Role, visibleRoles: VeboxModels.Role[],
                            manageableRoles: VeboxModels.Role[],
                            selectedRoles: generic_value[],
                            isAdminRoleChecked: boolean): boolean => {

    return currentRole === undefined
        || currentRole === null
        || isIncludedInOrIncludesAtLeastOneRole(
            currentRole,
            selectedRoles
                .filter((r: string) => r !== currentRole.code)
                .map((r) => getRoleFromRoleCode(r as string, visibleRoles)),
            true)
        || !isManageableRole(currentRole, manageableRoles)
        || isAdminRoleChecked;
}

/**
 * Returns the correct message according to the reason for disabling
 * @param currentRole
 * @param isDisabled
 * @param manageableRoles
 * @param roleContainedInAnother
 * @param roleNotManageable
 */
export const getDisableMessage = (currentRole: VeboxModels.Role, isDisabled: boolean,
                                  manageableRoles: VeboxModels.Role[],
                                  roleContainedInAnother: string, roleNotManageable): string => {
    return isDisabled && isManageableRole(currentRole, manageableRoles)
        ? roleContainedInAnother
        : roleNotManageable
}

export const isEndUserAdmin = (user: VeboxModels.User): boolean => {
    return user?.rolesAndPermissions.findIndex((role) => role.code === ROLE_END_USER_ADMIN) > -1
}

export const isUserAdmin = (user: VeboxModels.User): boolean => {
    return user?.rolesAndPermissions.findIndex((role) => role.code === ROLE_ADMIN) > -1
}

export const isReadOnlyUser = (user: VeboxModels.User): boolean => {
    return user?.rolesAndPermissions.findIndex((r) =>
        r.permissions.findIndex((p) => p.code === MANAGE_USERS) > -1) === -1;
}

export const canEdit = (user: VeboxModels.User): boolean => {
    return !isReadOnlyUser(user);
}

export const userHasPermission = (user: VeboxModels.User, permissionCode: string): boolean => {
    return user?.rolesAndPermissions.some((r) =>
        r?.permissions.some((p) => p?.code === permissionCode));
}
